import React, { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { Link, useSearchParams } from 'react-router-dom'
import { 
  ArrowLeft,
  Check,
  Gift,
  Star,
  ShoppingBag,
  Calendar,
  Truck
} from 'lucide-react'
import { loadMomentSettings, generateMomentSDKConfig } from '../utils/momentSettings'

const OrderConfirmationPage: React.FC = () => {
  const [searchParams] = useSearchParams()
  const productId = searchParams.get('product') || '1'
  
  // Debug state
  // State to track SDK loading and initialization status (used internally, debug UI removed)
  const [_debugInfo, setDebugInfo] = useState({
    sdkLoaded: false,
    configSet: false,
    userSet: false,
    initCalled: false,
    offersReceived: false,
    lastError: null as string | null,
    adpxExists: false,
    configData: null as any,
    userData: null as any,
    showDebug: false
  })

  const products = {
    '1': {
      name: "Apple AirPods Pro (2nd Gen)",
      price: 249.99,
      originalPrice: 299.99,
      image: "https://images.unsplash.com/photo-1600294037681-c80b4cb5b434?w=300&h=300&fit=crop",
      rating: 4.9,
      reviews: 2847
    },
    '2': {
      name: "Samsung Galaxy Watch 6",
      price: 329.99,
      originalPrice: 399.99,
      image: "https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=300&h=300&fit=crop",
      rating: 4.7,
      reviews: 1892
    },
    '3': {
      name: "MacBook Air M3 13-inch",
      price: 1299.99,
      originalPrice: 1499.99,
      image: "https://images.unsplash.com/photo-1517336714731-489689fd1ca8?w=300&h=300&fit=crop",
      rating: 4.8,
      reviews: 3634
    },
    '4': {
      name: "iPhone 15 Pro Max",
      price: 1199.99,
      originalPrice: 1299.99,
      image: "https://images.unsplash.com/photo-1592750475338-74b7b21085ab?w=300&h=300&fit=crop",
      rating: 4.9,
      reviews: 5445
    },
    '5': {
      name: "Sony WH-1000XM5 Headphones",
      price: 379.99,
      originalPrice: 429.99,
      image: "https://images.unsplash.com/photo-1546435770-a3e426bf472b?w=300&h=300&fit=crop",
      rating: 4.8,
      reviews: 2723
    },
    '6': {
      name: "iPad Pro 12.9-inch M4",
      price: 1099.99,
      originalPrice: 1199.99,
      image: "https://images.unsplash.com/photo-1544244015-0df4b3ffc6b0?w=300&h=300&fit=crop",
      rating: 4.9,
      reviews: 1556
    },
    '7': {
      name: "Gaming Mechanical Keyboard",
      price: 159.99,
      originalPrice: 199.99,
      image: "https://images.unsplash.com/photo-1541140532154-b024d705b90a?w=300&h=300&fit=crop",
      rating: 4.6,
      reviews: 892
    },
    '8': {
      name: "4K Webcam for Streaming",
      price: 129.99,
      originalPrice: 169.99,
      image: "https://images.unsplash.com/photo-1587831990711-23ca6441447b?w=300&h=300&fit=crop",
      rating: 4.5,
      reviews: 634
    },
    '9': {
      name: "Wireless Charging Station",
      price: 79.99,
      originalPrice: 99.99,
      image: "https://images.unsplash.com/photo-1586953208448-b95a79798f07?w=300&h=300&fit=crop",
      rating: 4.4,
      reviews: 445
    }
  }

  // Initialize MomentScience SDK
  useEffect(() => {
    // Load saved customization settings
    const savedSettings = loadMomentSettings()
    
    // Generate SDK configuration with saved settings
    const sdkConfig = generateMomentSDKConfig(savedSettings)
    
    // Configure MomentScience SDK with customized settings
    ;(window as any).AdpxConfig = sdkConfig

    // Set user payload for personalization
    const userData = {
      placement: 'post_transaction',
      theme_id: 'moment_post_transaction',
      product_id: productId,
      order_value: products[productId as keyof typeof products]?.price || 0,
      currency: 'USD',
      country: 'US'
    }
    ;(window as any).AdpxUser = userData

    // Update debug info
    setDebugInfo(prev => ({
      ...prev,
      configSet: true,
      userSet: true,
      configData: sdkConfig,
      userData: userData
    }))

    console.log('MomentScience SDK initialized with customized settings:', sdkConfig)

    // Load and initialize the SDK
    const loadMomentScience = async () => {
      try {
        const target = document.head || document.body;
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = 'https://cdn.pubtailer.com/launcher.min.js';
        script.crossOrigin = 'anonymous';
        script.async = true;
        script.id = 'adpx-launcher';
        
        target.appendChild(script);
        
        await new Promise((resolve, reject) => {
          if ((window as any).Adpx) {
            resolve(null);
          } else {
            script.addEventListener('load', () => {
              setDebugInfo(prev => ({ ...prev, sdkLoaded: true, adpxExists: !!(window as any).Adpx }))
              resolve(null);
            });
            script.addEventListener('error', (e) => {
              setDebugInfo(prev => ({ ...prev, lastError: 'Failed to load SDK script' }))
              reject(e);
            });
          }
        });
        
        if ((window as any).Adpx) {
          (window as any).Adpx.init((window as any).AdpxConfig);
          setDebugInfo(prev => ({ 
            ...prev, 
            initCalled: true,
            adpxExists: true,
            sdkLoaded: true
          }))
          
          // Listen for offers
          setTimeout(() => {
            setDebugInfo(prev => ({ ...prev, offersReceived: true }))
          }, 2000)
        }
      } catch (error) {
        setDebugInfo(prev => ({ 
          ...prev, 
          lastError: error instanceof Error ? error.message : 'Unknown error'
        }))
        console.error('Error loading MomentScience SDK:', error)
      }
    };

    loadMomentScience();

    // Cleanup function
    return () => {
      const existingScript = document.getElementById('adpx-launcher');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, [productId]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  }

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: 'easeOut',
      },
    },
  }

  const product = products[productId as keyof typeof products] || products['1']
  const orderNumber = `TH${Date.now().toString().slice(-6)}`
  const estimatedDelivery = new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  })

  return (
    <div className="min-h-screen bg-[#F9F9F9]">
      {/* Navigation */}
      <nav className="p-6 bg-white shadow-sm">
        <div className="container mx-auto flex items-center justify-between">
          <Link
            to="/product-selection"
            className="inline-flex items-center space-x-2 text-[#6E6D7A] hover:text-[#3B82F6] transition-colors duration-200 font-sans"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Continue Shopping</span>
          </Link>
          
          <div className="flex items-center space-x-2">
            <ShoppingBag className="w-6 h-6 text-[#FE4215]" />
            <span className="text-[#1A2B49] font-medium font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
              TechHub Electronics
            </span>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="max-w-3xl mx-auto"
        >
          {/* Success Header */}
          <motion.div variants={itemVariants} className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
              <Check className="w-10 h-10 text-green-600" />
            </div>
            
            <h1 className="text-4xl font-bold text-[#1A2B49] mb-4 font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
              Order Confirmed! 🎉
            </h1>
            
            <p className="text-lg text-[#6E6D7A] font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
              Thank you for your purchase. Your order has been successfully placed.
            </p>
          </motion.div>

          {/* MomentScience Settings Notice */}
          <motion.div variants={itemVariants} className="mb-6">
            <div className="bg-gradient-to-r from-[#FE4215]/10 to-[#233D71]/10 border border-[#FE4215]/20 rounded-lg p-4">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-[#FE4215] rounded-full flex items-center justify-center">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-[#1A2B49] font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
                    <strong>MomentScience Customization Active:</strong> Your personalized Moment settings have been applied to this page.
                  </p>
                  <p className="text-xs text-[#6E6D7A] font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
                    Any offers displayed will use your customized appearance, behavior, and style preferences.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Order Details */}
          <motion.div
            variants={itemVariants}
            className="bg-white rounded-lg p-6 mb-6"
            style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)', borderRadius: '12px' }}
          >
            <h2 className="text-xl font-bold mb-4 text-[#1A2B49] font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
              Order Details
            </h2>
            
            <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200">
              <span className="text-[#6E6D7A] font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
                Order Number:
              </span>
              <span className="font-medium text-[#1A2B49] font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
                #{orderNumber}
              </span>
            </div>
            
            <div className="flex items-center space-x-4">
              <img 
                src={product.image} 
                alt={product.name}
                className="w-20 h-20 object-cover rounded-lg"
              />
              <div className="flex-1">
                <h3 className="font-medium text-[#1A2B49] font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
                  {product.name}
                </h3>
                <div className="flex items-center space-x-2 mt-1">
                  <div className="flex items-center">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className={`w-4 h-4 ${i < Math.floor(product.rating) ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} />
                    ))}
                  </div>
                  <span className="text-sm text-[#6E6D7A] font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
                    ({product.reviews} reviews)
                  </span>
                </div>
                <div className="flex items-center space-x-2 mt-2">
                  <span className="text-lg font-bold text-[#FE4215] font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
                    ${product.price}
                  </span>
                  <span className="text-sm text-[#6E6D7A] line-through font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
                    ${product.originalPrice}
                  </span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Delivery Information */}
          <motion.div
            variants={itemVariants}
            className="bg-white rounded-lg p-6 mb-6"
            style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)', borderRadius: '12px' }}
          >
            <h2 className="text-xl font-bold mb-4 text-[#1A2B49] font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
              Delivery Information
            </h2>
            
            <div className="flex items-center space-x-4 mb-4">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <Truck className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-medium text-[#1A2B49] font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Free Standard Delivery
                </h3>
                <p className="text-[#6E6D7A] font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Your order will be delivered to your address
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <Calendar className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="font-medium text-[#1A2B49] font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Estimated Delivery
                </h3>
                <p className="text-[#6E6D7A] font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
                  {estimatedDelivery}
                </p>
              </div>
            </div>
          </motion.div>

          {/* Embedded Moments Container */}
          <motion.div
            variants={itemVariants}
            className="mb-6"
          >
            <div 
              id="adpx"
              className="min-h-[200px] rounded-lg bg-gradient-to-br from-gray-50 to-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center transition-all duration-300"
            >
              <div className="text-center p-8">
                <div className="w-12 h-12 bg-gradient-to-br from-[#FE4215] to-[#233D71] rounded-full flex items-center justify-center mx-auto mb-4">
                  <Gift className="w-6 h-6 text-white" />
                </div>
                <div className="text-lg font-medium text-gray-600 mb-2">Embedded Moments Container</div>
                <div className="text-sm text-gray-500 mb-4">
                  When embedded mode is enabled, offers will appear here
                </div>
                <div className="inline-flex items-center space-x-2 text-xs text-gray-400 bg-white px-3 py-2 rounded-full">
                  <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                  <span>ID: adpx</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Action Buttons */}
          <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/"
              className="flex-1 inline-flex items-center justify-center px-6 py-3 bg-[#233D71] text-white rounded-lg hover:bg-[#1A2B49] transition-colors font-sans"
              style={{ 
                fontFamily: 'Inter, sans-serif',
                borderRadius: '8px'
              }}
            >
              Back to Home
            </Link>
            
            <Link
              to="/product-selection"
              className="flex-1 inline-flex items-center justify-center px-6 py-3 bg-[#FE4215] text-white rounded-lg hover:bg-[#E53910] transition-colors font-sans"
              style={{ 
                fontFamily: 'Inter, sans-serif',
                borderRadius: '8px'
              }}
            >
              Continue Shopping
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </div>
  )
}

export default OrderConfirmationPage
